import React, { FC, useContext, useEffect, useState, useMemo } from 'react';
import { Navigate, Routes as ReactRoutes, Route, Outlet, useNavigate } from 'react-router-dom';

import { isEmptyObj } from 'src/common/helpers';
import { LoadingPlaceholder } from 'src/components/core/loading-placeholder/LoadingPlaceholder';
import { useAuth } from 'src/context/AuthContext';
import { useAppSelector } from 'src/hooks';
import { useKycAndFeaturesConfig } from 'src/hooks/useKycAndFeaturesConfig';
import DerivativeWrapper from 'src/pages/derivatives/DerivativeWrapper';
import { NotFoundPage } from 'src/pages/not-found';
import SpotWrapper from 'src/pages/spot/SpotWrapper';
import { RootState } from 'src/setup';
import { APP_URLS } from 'src/setup/client/app-urls';
import SocketContext from 'src/setup/socket/socketContext';

export const RequireOnboardingAllowedGuard = () => {
    const { isAuthenticated } = useAuth();
    const { allowOnboarding } = useKycAndFeaturesConfig();
    const navigate = useNavigate();

    const { userKycConfig, userFeaturesConfig, userVerifyInfoDirty } = useAppSelector(
        (state: RootState) => state.user,
    );

    const loadingState = useMemo(() => {
        return (
            isAuthenticated &&
            (!userVerifyInfoDirty || isEmptyObj(userKycConfig) || isEmptyObj(userFeaturesConfig))
        );
    }, [userVerifyInfoDirty, userKycConfig, userFeaturesConfig, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && !allowOnboarding && !loadingState) {
            navigate(APP_URLS.ONBOARDING_DISABLED);
            return;
        }
    }, [isAuthenticated, allowOnboarding, loadingState]);

    if (loadingState) {
        return <LoadingPlaceholder />;
    }

    return <Outlet />;
};

const Routes: FC = () => {
    const { sockets } = useContext(SocketContext);
    const [mainComponentKey, setMainComponentKey] = useState(0);

    useEffect(() => {
        // TODO:: Needs to handle in better way
        const handleVisibilityChange = () => {
            if (isEmptyObj(sockets)) {
                setMainComponentKey((prev) => prev + 1);
            }
        };
        window.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [sockets]);

    return (
        <ReactRoutes key={mainComponentKey}>
            <Route path="/" element={<Navigate to={'/contract'} replace />} />

            <Route element={<RequireOnboardingAllowedGuard />}>
                <Route path={`${APP_URLS.SPOT}/:symbolId?`} element={<SpotWrapper />} />
                <Route path={`${APP_URLS.CONTRACT}/:symbolId?`} element={<DerivativeWrapper />} />
            </Route>

            <Route path="/*" element={<NotFoundPage />} />
        </ReactRoutes>
    );
};

export default Routes;
